import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Percent, Users, Ban, CheckCircle, Banknote, Link2, RefreshCw } from 'lucide-react';
import { REFERRAL_LIST_LIMIT, AFFILIATE_LIST_LIMIT } from '../../config/affiliateConstants';

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

const TABS = [
    { id: 'payouts', label: 'Payouts', icon: Banknote },
    { id: 'affiliates', label: 'Affiliates', icon: Users },
    { id: 'referrals', label: 'Referrals', icon: Link2 },
    { id: 'settings', label: 'Settings', icon: Percent }
];

const formatMoney = (amount, currency = 'GBP') => {
    const num = parseFloat(amount);
    if (Number.isNaN(num)) return '—';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(num);
};

const formatReferredName = (c) => {
    if (c.referred_studio_name) return c.referred_studio_name;
    if (c.referred_owner_name) return c.referred_owner_name;
    return c.referred_email || '—';
};

const formatAffiliateName = (c) => {
    if (c.affiliate_studio_name) return c.affiliate_studio_name;
    if (c.affiliate_staff_name) return c.affiliate_staff_name;
    return c.affiliate_code || '—';
};

export default function AffiliateSettings() {
    const [activeTab, setActiveTab] = useState('payouts');
    const [commissionRate, setCommissionRate] = useState('20');
    const [affiliates, setAffiliates] = useState([]);
    const [affiliatesTotal, setAffiliatesTotal] = useState(0);
    const [affiliatePage, setAffiliatePage] = useState(1);
    const [loadingAffiliates, setLoadingAffiliates] = useState(false);
    const [referrals, setReferrals] = useState([]);
    const [referralsTotal, setReferralsTotal] = useState(0);
    const [referralPage, setReferralPage] = useState(1);
    const [loadingReferrals, setLoadingReferrals] = useState(false);
    const [commissions, setCommissions] = useState([]);
    const [commissionsTotal, setCommissionsTotal] = useState(0);
    const [commissionPage, setCommissionPage] = useState(1);
    const [commissionStatusFilter, setCommissionStatusFilter] = useState('pending');
    const [commissionStats, setCommissionStats] = useState(null);
    const [loadingCommissions, setLoadingCommissions] = useState(false);
    const [markingPaidId, setMarkingPaidId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [tabsLoaded, setTabsLoaded] = useState({ payouts: false, affiliates: false, referrals: false });

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (loading) return;
        loadActiveTabData(activeTab);
    }, [activeTab, loading]);

    const loadAffiliates = async (page = 1) => {
        setLoadingAffiliates(true);
        try {
            const offset = (page - 1) * AFFILIATE_LIST_LIMIT;
            const affiliatesRes = await axios.get(`${API_URL}/admin/affiliates`, {
                params: { limit: AFFILIATE_LIST_LIMIT, offset }
            });
            setAffiliates(affiliatesRes.data.affiliates || []);
            setAffiliatesTotal(affiliatesRes.data.total ?? 0);
            setAffiliatePage(page);
        } catch (error) {
            console.error('Failed to load affiliates', error);
        } finally {
            setLoadingAffiliates(false);
        }
    };

    const loadReferrals = async (page = 1) => {
        setLoadingReferrals(true);
        try {
            const offset = (page - 1) * REFERRAL_LIST_LIMIT;
            const referralsRes = await axios.get(`${API_URL}/admin/referrals`, {
                params: { limit: REFERRAL_LIST_LIMIT, offset }
            });
            setReferrals(referralsRes.data.referrals || []);
            setReferralsTotal(referralsRes.data.total ?? 0);
            setReferralPage(page);
        } catch (error) {
            console.error('Failed to load referrals', error);
        } finally {
            setLoadingReferrals(false);
        }
    };

    const loadCommissions = async (page = 1, status = commissionStatusFilter) => {
        setLoadingCommissions(true);
        try {
            const offset = (page - 1) * REFERRAL_LIST_LIMIT;
            const res = await axios.get(`${API_URL}/admin/commissions`, {
                params: { limit: REFERRAL_LIST_LIMIT, offset, status }
            });
            setCommissions(res.data.commissions || []);
            setCommissionsTotal(res.data.total ?? 0);
            setCommissionPage(page);
            setCommissionStats({
                pending_count: res.data.pending_count ?? 0,
                pending_total: res.data.pending_total ?? 0,
                paid_count: res.data.paid_count ?? 0,
                paid_total: res.data.paid_total ?? 0,
                currency: res.data.currency || 'GBP'
            });
        } catch (error) {
            console.error('Failed to load commissions', error);
        } finally {
            setLoadingCommissions(false);
        }
    };

    const loadCommissionStats = async () => {
        try {
            const res = await axios.get(`${API_URL}/admin/commissions`, {
                params: { limit: 1, offset: 0, status: 'pending' }
            });
            setCommissionStats({
                pending_count: res.data.pending_count ?? 0,
                pending_total: res.data.pending_total ?? 0,
                paid_count: res.data.paid_count ?? 0,
                paid_total: res.data.paid_total ?? 0,
                currency: res.data.currency || 'GBP'
            });
        } catch (error) {
            console.error('Failed to load commission stats', error);
        }
    };

    const loadActiveTabData = async (tab) => {
        if (tab === 'payouts' && !tabsLoaded.payouts) {
            await loadCommissions(1, 'pending');
            setTabsLoaded((prev) => ({ ...prev, payouts: true }));
        } else if (tab === 'affiliates' && !tabsLoaded.affiliates) {
            await loadAffiliates(1);
            setTabsLoaded((prev) => ({ ...prev, affiliates: true }));
        } else if (tab === 'referrals' && !tabsLoaded.referrals) {
            await loadReferrals(1);
            setTabsLoaded((prev) => ({ ...prev, referrals: true }));
        }
    };

    const loadInitialData = async () => {
        try {
            const [settingsRes, affiliatesRes, referralsRes] = await Promise.all([
                axios.get(`${API_URL}/admin/affiliate-settings`),
                axios.get(`${API_URL}/admin/affiliates`, { params: { limit: 1, offset: 0 } }),
                axios.get(`${API_URL}/admin/referrals`, { params: { limit: 1, offset: 0 } })
            ]);
            if (settingsRes.data.default_commission_rate != null) {
                setCommissionRate(String(settingsRes.data.default_commission_rate));
            }
            setAffiliatesTotal(affiliatesRes.data.total ?? 0);
            setReferralsTotal(referralsRes.data.total ?? 0);
            await loadCommissionStats();
        } catch (error) {
            console.error('Failed to load affiliate settings', error);
            setMessage({ type: 'error', text: 'Failed to load affiliate data' });
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setMessage(null);
    };

    const handleCommissionStatusChange = (status) => {
        setCommissionStatusFilter(status);
        loadCommissions(1, status);
    };

    const handleRetryPayout = async (commission) => {
        const amount = formatMoney(commission.commission_amount, commission.currency);
        const affiliate = formatAffiliateName(commission);

        if (!commission.stripe_connect_id) {
            setMessage({
                type: 'error',
                text: `${affiliate} has no bank/Connect account yet. Ask them to add bank details on server.bookink.uk.`
            });
            return;
        }

        if (!window.confirm(`Retry Stripe payout of ${amount} to ${affiliate}?`)) return;

        setMarkingPaidId(commission.id);
        setMessage(null);
        try {
            const res = await axios.post(`${API_URL}/admin/commissions/${commission.id}/retry-payout`);
            setMessage({ type: 'success', text: res.data.message || `Payout sent: ${amount}` });
            await loadCommissions(commissionPage, commissionStatusFilter);
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to retry payout' });
        } finally {
            setMarkingPaidId(null);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const rate = parseFloat(commissionRate);
        if (isNaN(rate) || rate < 0 || rate > 100) {
            setMessage({ type: 'error', text: 'Enter a rate between 0 and 100' });
            return;
        }
        setSaving(true);
        setMessage(null);
        try {
            await axios.put(`${API_URL}/admin/affiliate-settings`, {
                default_commission_rate: rate
            });
            setMessage({ type: 'success', text: 'Commission rate saved' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save' });
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
        const label = newStatus === 'suspended' ? 'suspend' : 'reactivate';
        if (!window.confirm(`Are you sure you want to ${label} this affiliate?`)) return;
        try {
            await axios.put(`${API_URL}/admin/affiliates/${id}/status`, { status: newStatus });
            setMessage({ type: 'success', text: `Affiliate ${newStatus}` });
            loadAffiliates(affiliatePage);
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update status' });
        }
    };

    const getTabBadge = (tabId) => {
        if (tabId === 'payouts' && commissionStats?.pending_count > 0) {
            return commissionStats.pending_count;
        }
        if (tabId === 'affiliates' && affiliatesTotal > 0) return affiliatesTotal;
        if (tabId === 'referrals' && referralsTotal > 0) return referralsTotal;
        return null;
    };

    const totalAffiliatePages = Math.max(1, Math.ceil(affiliatesTotal / AFFILIATE_LIST_LIMIT));
    const affiliateRangeStart = affiliatesTotal === 0 ? 0 : (affiliatePage - 1) * AFFILIATE_LIST_LIMIT + 1;
    const affiliateRangeEnd = Math.min(affiliatePage * AFFILIATE_LIST_LIMIT, affiliatesTotal);
    const totalReferralPages = Math.max(1, Math.ceil(referralsTotal / REFERRAL_LIST_LIMIT));
    const referralRangeStart = referralsTotal === 0 ? 0 : (referralPage - 1) * REFERRAL_LIST_LIMIT + 1;
    const referralRangeEnd = Math.min(referralPage * REFERRAL_LIST_LIMIT, referralsTotal);
    const totalCommissionPages = Math.max(1, Math.ceil(commissionsTotal / REFERRAL_LIST_LIMIT));
    const commissionRangeStart = commissionsTotal === 0 ? 0 : (commissionPage - 1) * REFERRAL_LIST_LIMIT + 1;
    const commissionRangeEnd = Math.min(commissionPage * REFERRAL_LIST_LIMIT, commissionsTotal);
    const statsCurrency = commissionStats?.currency || 'GBP';

    if (loading) {
        return <div className="text-gray-400 p-4">Loading affiliate settings...</div>;
    }

    return (
        <div className="space-y-6">
            {message && (
                <div className={`px-4 py-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-900/30 border border-green-700 text-green-300' : 'bg-red-900/30 border border-red-700 text-red-300'}`}>
                    {message.text}
                </div>
            )}

            <div className="flex gap-2 sm:gap-4 border-b border-gray-700 overflow-x-auto no-scrollbar whitespace-nowrap">
                {TABS.map(({ id, label, icon: Icon }) => {
                    const badge = getTabBadge(id);
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => handleTabChange(id)}
                            className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                                activeTab === id
                                    ? 'text-purple-400 border-b-2 border-purple-500'
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                        >
                            <Icon size={14} />
                            {label}
                            {badge != null && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    id === 'payouts' && commissionStats?.pending_count > 0
                                        ? 'bg-amber-900/50 text-amber-300'
                                        : 'bg-gray-700 text-gray-300'
                                }`}>
                                    {badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'settings' && (
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <h2 className="text-lg font-bold mb-1 flex items-center gap-2">
                        <Percent size={20} className="text-purple-400" />
                        Default Commission Rate
                    </h2>
                    <p className="text-gray-400 text-sm mb-6">
                        Applied to all affiliates when bookink.uk sends a successful subscription payment.
                    </p>
                    <form onSubmit={handleSave} className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                Commission %
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    value={commissionRate}
                                    onChange={(e) => setCommissionRate(e.target.value)}
                                    className="w-32 bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                />
                                <span className="text-gray-400 font-bold">%</span>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wider"
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </form>
                </div>
            )}

            {activeTab === 'payouts' && (
                <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-700">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <Banknote size={20} className="text-amber-400" />
                            Commission payouts
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Review pending affiliate earnings and mark them as paid after payout
                        </p>
                        {commissionStats && (
                            <div className="flex flex-wrap gap-4 mt-4 text-sm">
                                <span className="text-amber-300 font-medium">
                                    Pending: {formatMoney(commissionStats.pending_total, statsCurrency)}
                                    <span className="text-gray-500 font-normal ml-1">({commissionStats.pending_count})</span>
                                </span>
                                <span className="text-green-400 font-medium">
                                    Paid: {formatMoney(commissionStats.paid_total, statsCurrency)}
                                    <span className="text-gray-500 font-normal ml-1">({commissionStats.paid_count})</span>
                                </span>
                            </div>
                        )}
                        <div className="flex gap-2 mt-4">
                            {['pending', 'paid', 'all'].map((status) => (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => handleCommissionStatusChange(status)}
                                    className={`text-xs font-bold px-3 py-1.5 rounded capitalize ${
                                        commissionStatusFilter === status
                                            ? 'bg-purple-600 text-white'
                                            : 'border border-gray-600 text-gray-400 hover:bg-gray-700'
                                    }`}
                                >
                                    {status}
                                </button>
                            ))}
                        </div>
                    </div>
                    {loadingCommissions && commissions.length === 0 ? (
                        <p className="p-6 text-gray-500 text-sm">Loading commissions...</p>
                    ) : commissionsTotal === 0 ? (
                        <p className="p-6 text-gray-500 text-sm">
                            No {commissionStatusFilter === 'all' ? '' : `${commissionStatusFilter} `}commissions.
                        </p>
                    ) : (
                        <>
                            <div className={`overflow-x-auto ${loadingCommissions ? 'opacity-50' : ''}`}>
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="text-gray-500 border-b border-gray-700">
                                            <th className="p-4 w-12">#</th>
                                            <th className="p-4">Affiliate</th>
                                            <th className="p-4">Referred</th>
                                            <th className="p-4">Payment</th>
                                            <th className="p-4">Commission</th>
                                            <th className="p-4">Status</th>
                                            <th className="p-4">Date</th>
                                            {commissionStatusFilter !== 'paid' && <th className="p-4">Action</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {commissions.map((c, index) => (
                                            <tr key={c.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                                                <td className="p-4 text-gray-500">
                                                    {(commissionPage - 1) * REFERRAL_LIST_LIMIT + index + 1}
                                                </td>
                                                <td className="p-4">
                                                    <div className="font-medium">{formatAffiliateName(c)}</div>
                                                    <div className="text-xs text-gray-500 font-mono">{c.affiliate_code}</div>
                                                </td>
                                                <td className="p-4">
                                                    <div>{formatReferredName(c)}</div>
                                                    {c.referred_email && (
                                                        <div className="text-xs text-gray-500">{c.referred_email}</div>
                                                    )}
                                                </td>
                                                <td className="p-4 text-gray-300">
                                                    {formatMoney(c.payment_amount, c.currency)}
                                                </td>
                                                <td className="p-4 font-medium text-amber-300">
                                                    {formatMoney(c.commission_amount, c.currency)}
                                                    <span className="text-xs text-gray-400 ml-1">({c.commission_rate}%)</span>
                                                </td>
                                                <td className="p-4">
                                                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                                                        c.status === 'paid'
                                                            ? 'bg-green-900/40 text-green-400'
                                                            : 'bg-amber-900/40 text-amber-400'
                                                    }`}>
                                                        {c.status}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-500">
                                                    <div>{new Date(c.created_at).toLocaleDateString()}</div>
                                                    {c.paid_at && (
                                                        <div className="text-xs text-green-500">
                                                            Paid {new Date(c.paid_at).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                </td>
                                                {commissionStatusFilter !== 'paid' && (
                                                    <td className="p-4">
                                                        {c.status === 'pending' ? (
                                                            <div className="space-y-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRetryPayout(c)}
                                                                    disabled={markingPaidId === c.id}
                                                                    className="text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1 bg-indigo-900/40 text-indigo-200 hover:bg-indigo-900/60 disabled:opacity-50"
                                                                >
                                                                    <RefreshCw size={12} className={markingPaidId === c.id ? 'animate-spin' : ''} />
                                                                    {markingPaidId === c.id ? 'Sending...' : 'Retry payout'}
                                                                </button>
                                                                {!c.stripe_connect_id && (
                                                                    <p className="text-[10px] text-amber-500/90 leading-snug max-w-[140px]">
                                                                        No Connect ID — bank needed on server.bookink.uk
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-gray-600">—</span>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalCommissionPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 bg-gray-900/40">
                                    <button
                                        type="button"
                                        onClick={() => loadCommissions(commissionPage - 1, commissionStatusFilter)}
                                        disabled={loadingCommissions || commissionPage <= 1}
                                        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-400">
                                        Page {commissionPage} of {totalCommissionPages}
                                        <span className="text-gray-500 ml-1">
                                            ({commissionRangeStart}–{commissionRangeEnd} of {commissionsTotal})
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => loadCommissions(commissionPage + 1, commissionStatusFilter)}
                                        disabled={loadingCommissions || commissionPage >= totalCommissionPages}
                                        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'affiliates' && (
                <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-700">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <Users size={20} className="text-purple-400" />
                            All Affiliates ({affiliatesTotal})
                        </h2>
                    </div>
                    {loadingAffiliates && affiliates.length === 0 ? (
                        <p className="p-6 text-gray-500 text-sm">Loading affiliates...</p>
                    ) : affiliatesTotal === 0 ? (
                        <p className="p-6 text-gray-500 text-sm">No affiliates yet.</p>
                    ) : (
                        <>
                            <div className={`overflow-x-auto ${loadingAffiliates ? 'opacity-50' : ''}`}>
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="text-gray-500 border-b border-gray-700">
                                            <th className="p-4 w-12">#</th>
                                            <th className="p-4">Name</th>
                                            <th className="p-4">Email</th>
                                            <th className="p-4">Code</th>
                                            <th className="p-4">Referrals</th>
                                            <th className="p-4">Status</th>
                                            <th className="p-4">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {affiliates.map((a, index) => (
                                            <tr key={a.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                                                <td className="p-4 text-gray-500">
                                                    {(affiliatePage - 1) * AFFILIATE_LIST_LIMIT + index + 1}
                                                </td>
                                                <td className="p-4 font-medium">
                                                    {a.studio_name || a.staff_name || '—'}
                                                </td>
                                                <td className="p-4 text-gray-400">
                                                    {a.studio_email || a.staff_email || '—'}
                                                </td>
                                                <td className="p-4 font-mono text-purple-300">{a.referral_code}</td>
                                                <td className="p-4 text-gray-300">{a.total_referrals ?? 0}</td>
                                                <td className="p-4">
                                                    <span className={`text-xs font-bold px-2 py-1 rounded ${a.status === 'active' ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                                                        {a.status}
                                                    </span>
                                                </td>
                                                <td className="p-4">
                                                    <button
                                                        onClick={() => toggleStatus(a.id, a.status)}
                                                        className={`text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1 ${a.status === 'active' ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-green-900/30 text-green-300 hover:bg-green-900/50'}`}
                                                    >
                                                        {a.status === 'active' ? <><Ban size={12} /> Suspend</> : <><CheckCircle size={12} /> Activate</>}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalAffiliatePages > 1 && (
                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 bg-gray-900/40">
                                    <button
                                        type="button"
                                        onClick={() => loadAffiliates(affiliatePage - 1)}
                                        disabled={loadingAffiliates || affiliatePage <= 1}
                                        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-400">
                                        Page {affiliatePage} of {totalAffiliatePages}
                                        <span className="text-gray-500 ml-1">
                                            ({affiliateRangeStart}–{affiliateRangeEnd} of {affiliatesTotal})
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => loadAffiliates(affiliatePage + 1)}
                                        disabled={loadingAffiliates || affiliatePage >= totalAffiliatePages}
                                        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'referrals' && (
                <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-700">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <Link2 size={20} className="text-indigo-400" />
                            Referral signups ({referralsTotal})
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Studios that registered on bookink.uk using an affiliate link
                        </p>
                    </div>
                    {loadingReferrals && referrals.length === 0 ? (
                        <p className="p-6 text-gray-500 text-sm">Loading referrals...</p>
                    ) : referralsTotal === 0 ? (
                        <p className="p-6 text-gray-500 text-sm">No referral signups yet.</p>
                    ) : (
                        <>
                            <div className={`overflow-x-auto ${loadingReferrals ? 'opacity-50' : ''}`}>
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="text-gray-500 border-b border-gray-700">
                                            <th className="p-4 w-12">#</th>
                                            <th className="p-4">Referred email</th>
                                            <th className="p-4">Code used</th>
                                            <th className="p-4">Affiliate</th>
                                            <th className="p-4">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {referrals.map((r, index) => (
                                            <tr key={r.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                                                <td className="p-4 text-gray-500">
                                                    {(referralPage - 1) * REFERRAL_LIST_LIMIT + index + 1}
                                                </td>
                                                <td className="p-4">{r.referred_email}</td>
                                                <td className="p-4 font-mono text-purple-300">{r.referral_code_used}</td>
                                                <td className="p-4 text-gray-400">
                                                    {r.affiliate_studio_name || r.affiliate_staff_name || r.affiliate_code}
                                                </td>
                                                <td className="p-4 text-gray-500">
                                                    {new Date(r.created_at).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalReferralPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 bg-gray-900/40">
                                    <button
                                        type="button"
                                        onClick={() => loadReferrals(referralPage - 1)}
                                        disabled={loadingReferrals || referralPage <= 1}
                                        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-400">
                                        Page {referralPage} of {totalReferralPages}
                                        <span className="text-gray-500 ml-1">
                                            ({referralRangeStart}–{referralRangeEnd} of {referralsTotal})
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => loadReferrals(referralPage + 1)}
                                        disabled={loadingReferrals || referralPage >= totalReferralPages}
                                        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
